import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from 'react';
import { SocketIoEvents } from '@/events/socket-io.events';
import { ReviewsAutoReplyProgress } from '@/context/reviews-page.context';
import type { AutoReplyProgressStage } from '@/entities/auto-reply-progress-event.entity';
import { getAutoReplyProgressCardLabels } from '@/shared/auto-replies-labels';
import { calculateEtaSeconds } from '@/shared/progress-estimation';
import { toast } from '@/ui/toast';

type UseAutoReplyProgressOrchestrationParams = {
    events: SocketIoEvents;
    setAutoReplyProgress: Dispatch<SetStateAction<ReviewsAutoReplyProgress | null>>;
};

type UseAutoReplyProgressOrchestrationReturn = {
    scheduleAutoReplyProgressCleanup: (operationId: string) => void;
};

const isTerminalStage = (stage: AutoReplyProgressStage) =>
    stage === 'completed' || stage === 'failed' || stage === 'canceled';

const toTerminalStage = (stage: AutoReplyProgressStage): ReviewsAutoReplyProgress['terminalStage'] =>
    isTerminalStage(stage) ? stage : null;

export const useAutoReplyProgressOrchestration = ({
    events,
    setAutoReplyProgress,
}: UseAutoReplyProgressOrchestrationParams): UseAutoReplyProgressOrchestrationReturn => {
    const cleanupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (cleanupTimerRef.current) {
                clearTimeout(cleanupTimerRef.current);
                cleanupTimerRef.current = null;
            }
        };
    }, []);

    const scheduleAutoReplyProgressCleanup = useCallback(
        (operationId: string) => {
            if (cleanupTimerRef.current) {
                clearTimeout(cleanupTimerRef.current);
            }

            cleanupTimerRef.current = setTimeout(() => {
                setAutoReplyProgress((current) => {
                    if (!current || current.operationId !== operationId) {
                        return current;
                    }

                    return null;
                });
                cleanupTimerRef.current = null;
            }, 120);
        },
        [setAutoReplyProgress],
    );

    useEffect(() => {
        const unsubscribe = events.on('autoReplyProgress', (data) => {
            const incomingIsTerminal = isTerminalStage(data.stage);

            if (data.stage === 'failed') {
                toast.error(data.errorMessage || getAutoReplyProgressCardLabels('ru').genericError);
            }

            setAutoReplyProgress((current) => {
                if (!current || current.operationId !== data.operationId) {
                    if (data.stage !== 'started') {
                        return current;
                    }

                    return {
                        isRunning: true,
                        operationId: data.operationId,
                        startedAt: data.startedAt,
                        total: data.total,
                        processed: data.processed,
                        successCount: data.successCount,
                        skippedCount: data.skippedCount,
                        failedCount: data.failedCount,
                        etaSeconds: null,
                        terminalStage: null,
                        errorMessage: null,
                    };
                }

                if (!current.isRunning && !incomingIsTerminal) {
                    return current;
                }

                const processed = Math.max(current.processed, data.processed);
                const total = Math.max(current.total, data.total);

                return {
                    ...current,
                    isRunning: !incomingIsTerminal,
                    startedAt: Math.min(current.startedAt, data.startedAt),
                    total,
                    processed,
                    successCount: Math.max(current.successCount, data.successCount),
                    skippedCount: Math.max(current.skippedCount, data.skippedCount),
                    failedCount: Math.max(current.failedCount, data.failedCount),
                    etaSeconds: incomingIsTerminal ? 0 : calculateEtaSeconds(current.startedAt, processed, total),
                    terminalStage: toTerminalStage(data.stage),
                    errorMessage: data.errorMessage,
                };
            });

            if (incomingIsTerminal) {
                scheduleAutoReplyProgressCleanup(data.operationId);
            }
        });

        return () => unsubscribe();
    }, [events, scheduleAutoReplyProgressCleanup, setAutoReplyProgress]);

    return {
        scheduleAutoReplyProgressCleanup,
    };
};
